import { SectionCard } from '@atlas/ui-components'
import { Chip } from '@atlas/ui-primitives'
import { Link } from 'react-router-dom'

/** One `order_code` this message carries, resolved to the order it names where that order is readable. */
export interface MessageOrderLink {
	orderCode: string
	/** `null` when no readable order carries this code — the link is shown, not silently dropped. */
	workOrderId: string | null
	title: string | null
}

/**
 * The work orders a message is ON, read from its `order_code` attribute.
 *
 * Its own card rather than a chip among the classification tags, because the two say
 * different things: the tuple describes what the copy is about, `order_code` is what
 * puts it in an order's Messages section. Rendered even when empty — "on no order" is
 * the state that makes copy invisible to every order-scoped surface, and it should be
 * readable here rather than inferred from an absent card.
 *
 * A code no readable order matches still shows: the message asserts the link, and
 * hiding it would make a typo or an order in another tenant look like no link at all.
 * Presentational + storied.
 */
export function MessageOrderLinks({ orders }: { orders: MessageOrderLink[] }) {
	return (
		<SectionCard title="Work orders" className="mt-4 p-6">
			{orders.length === 0 ? (
				<p className="text-sm text-ds-text-muted">
					Not on any work order — this copy will not appear in any order's Messages section.
				</p>
			) : (
				<div className="flex flex-wrap gap-1.5">
					{orders.map((order) =>
						order.workOrderId ? (
							<Link key={order.orderCode} to={`/work-orders/${order.workOrderId}`} className="rounded-ds-full">
								<Chip variant="secondary" size="sm" title={order.title ?? order.orderCode}>
									{order.orderCode}
									{order.title ? ` · ${order.title}` : ''}
								</Chip>
							</Link>
						) : (
							<Chip key={order.orderCode} variant="muted" size="sm" title="No readable work order carries this code">
								{order.orderCode}
							</Chip>
						),
					)}
				</div>
			)}
		</SectionCard>
	)
}
